import React, { useEffect, useState } from "react";
import { Button, Input, message, ColorPicker, Form } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { authSVC } from "../../services";
import { useB2BContext } from "../../context/ContextB2B";
import type { ColorPickerProps } from "antd";
import "./AddDepartments.scss";

const departmentColors = [
  "#479751", "#CB2E89", "#9EC756", "#D476AA",
  "#F5EE56", "#65529C", "#EBB949", "#84559D",
  "#E6893C", "#233264", "#D56135", "#9891C2",
  "#D94834", "#3170B5", "#B93347", "#4FA1A5",
];

function AddDepartments() {
  const { getUserByID } = useB2BContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [allDepartments, setAllDepartments] = useState<any[]>([]);
  const [colorValue, setColorValue] = useState<string>("");
  const [form] = Form.useForm();

  useEffect(() => {
    getDepartments();
  }, []);

  const getDepartments = () => {
    authSVC
      .getCompanyDepartments(getUserByID?.company)
      .then((res: any) => setAllDepartments(res.data.data))
      .catch((err: any) => message.error(err.message));
  };

  const onFinish = (values: any) => {
    if (!getUserByID?.company) {
      message.error("Company not found");
      return;
    }

    const departmentName = values.name;
    setLoading(true);

    const existingDepartment = allDepartments?.find(
      (dept: any) => dept.name === departmentName
    );

    if (existingDepartment) {
      message.info("Department already exists.");
      setLoading(false);
      return;
    }

    authSVC
      .addCompanyDepartments({
        companyName: getUserByID.company,
        departments: [{ name: departmentName, color: colorValue }],
      })
      .then(() => {
        message.success("Department added successfully!");
        form.resetFields();
        setColorValue("");
        getDepartments();
      })
      .catch((err: any) => message.error(err.message))
      .finally(() => setLoading(false));
  };

  const nameValidation = (_: any, value: any) => {
    const regex = /^[A-Za-z\s]+$/;
    if (!value || regex.test(value)) return Promise.resolve();
    return Promise.reject(
      new Error("Please enter a valid name with only letters.")
    );
  };

  const handleSwatchClick = (color: string) => {
    setColorValue(color);
    setLoading(false);
    form.setFieldsValue({ color });
    form.validateFields(["color"]);
  };

  const handleCustomColor = (color: any) => {
    const hex = color.toHexString();
    setColorValue(hex);
    form.setFieldsValue({ color: hex });
    form.validateFields(["color"]);
  };

  return (
    <div
      className="add-dept"
      style={{ "--active-color": colorValue || "#3170B5" } as React.CSSProperties}
    >
      <div className="add-dept__accent-bar" />

      <button
        type="button"
        className="add-dept__back"
        onClick={() => navigate(-1)}
      >
        <ArrowLeftOutlined /> Back
      </button>

      <h1 className="add-dept__title">Create Department</h1>
      <p className="add-dept__subtitle">
        Organise your team by adding departments below.
      </p>

      <div className={`add-dept__preview${colorValue ? " add-dept__preview--active" : ""}`}>
        <span
          className="add-dept__preview-dot"
          style={{ backgroundColor: colorValue || undefined }}
        />
        <span className="add-dept__preview-label">
          {colorValue ? "Department colour selected" : "No colour selected yet"}
        </span>
        {colorValue && (
          <span className="add-dept__preview-hex">{colorValue.toUpperCase()}</span>
        )}
      </div>

      <Form
        name="add_department"
        initialValues={{ remember: true }}
        onFinish={onFinish}
        form={form}
      >
        <Form.Item
          name="name"
          rules={[
            { required: true, message: "Please enter a department name." },
            { validator: nameValidation },
          ]}
        >
          <Input
            type="text"
            placeholder="Please Enter Department Name"
            className="add-dept__input"
            onChange={() => setLoading(false)}
          />
        </Form.Item>

        <p className="add-dept__section-label">Pick a colour</p>

        <Form.Item
          name="color"
          style={{ marginBottom: 8 }}
          rules={[{ required: true, message: "Please select a department colour." }]}
        >
          <div className="add-dept__color-grid">
            {departmentColors.map((color, index) => (
              <button
                key={index}
                type="button"
                className={`add-dept__swatch${colorValue === color ? " add-dept__swatch--active" : ""}`}
                style={{ backgroundColor: color }}
                onClick={() => handleSwatchClick(color)}
                aria-label={`Select colour ${color}`}
              />
            ))}
          </div>

          <div className="add-dept__custom-row">
            <span className="add-dept__custom-label">Custom</span>
            <ColorPicker
              value={colorValue || undefined}
              onChangeComplete={handleCustomColor}
            />
          </div>
        </Form.Item>

        <p className="add-dept__hint">
          Creating departments helps personalise wellness tracking for each team.
          You can always update colours later.
        </p>

        <div className="add-dept__actions">
          <Button
            className="add-dept__cancel"
            htmlType="button"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button
            className="add-dept__submit"
            htmlType="submit"
            loading={loading}
          >
            Create Department
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default AddDepartments;